import { Injectable } from '@angular/core';

export interface ExportOptions {
	filename: string;
	mimeType: string;
	content: Blob | string | ArrayBuffer;
	suggestedName?: string;
}

export interface ExportResult {
	success: boolean;
	method: 'save-picker' | 'download' | 'share';
	error?: string;
}

interface FilePickerAcceptType {
	description?: string;
	accept: Record<string, string[]>;
}

@Injectable({
	providedIn: 'root'
})
export class FileExportService {

	/**
	 * Prüft ob die File System Access API (showSaveFilePicker) verfügbar ist
	 */
	get canUseSavePicker(): boolean {
		return 'showSaveFilePicker' in window;
	}

	/**
	 * Prüft ob die Web Share API verfügbar ist und Dateien teilen kann
	 */
	get canShare(): boolean {
		if (!('share' in navigator && 'canShare' in navigator)) {
			return false;
		}
		return this.isMobileDevice();
	}

	private isMobileDevice(): boolean {
		const userAgent = navigator.userAgent.toLowerCase();
		const mobileKeywords = ['android', 'iphone', 'ipad', 'ipod', 'webos', 'blackberry', 'windows phone'];
		if (mobileKeywords.some(keyword => userAgent.includes(keyword))) {
			return true;
		}
		const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
		const isSmallScreen = window.innerWidth <= 768;
		return hasTouch && isSmallScreen;
	}

	canShareFile(file: File): boolean {
		if (!this.canShare) return false;
		try {
			return navigator.canShare({ files: [file] });
		} catch {
			return false;
		}
	}

	private createBlob(content: Blob | string | ArrayBuffer, mimeType: string): Blob {
		if (content instanceof Blob) {
			return content;
		}
		return new Blob([content], { type: mimeType });
	}

	private createFile(blob: Blob, filename: string): File {
		return new File([blob], filename, { type: blob.type });
	}

	private getExtensionFromMime(mimeType: string): string {
		const mimeToExt: Record<string, string> = {
			'application/pdf': '.pdf',
			'application/json': '.json',
			'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
			'application/vnd.ms-excel': '.xls',
			'text/plain': '.txt',
			'text/csv': '.csv',
			'application/geo+json': '.geojson',
			'image/png': '.png',
			'image/jpeg': '.jpg'
		};
		return mimeToExt[mimeType] || '';
	}

	private getFilePickerAcceptType(mimeType: string, extension: string): FilePickerAcceptType[] {
		const descriptions: Record<string, string> = {
			'application/pdf': 'PDF-Dokument',
			'application/json': 'JSON-Datei',
			'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel-Arbeitsmappe',
			'text/plain': 'Textdatei',
			'text/csv': 'CSV-Datei',
			'application/geo+json': 'GeoJSON-Datei'
		};

		return [{
			description: descriptions[mimeType] || 'Datei',
			accept: { [mimeType]: [extension] }
		}];
	}

	async saveWithPicker(options: ExportOptions): Promise<ExportResult> {
		const blob = this.createBlob(options.content, options.mimeType);
		const extension = this.getExtensionFromMime(options.mimeType);

		if (this.canUseSavePicker) {
			try {
				const handle = await (window as any).showSaveFilePicker({
					suggestedName: options.suggestedName || options.filename,
					types: this.getFilePickerAcceptType(options.mimeType, extension)
				});

				const writable = await handle.createWritable();
				await writable.write(blob);
				await writable.close();

				return { success: true, method: 'save-picker' };
			} catch (e: any) {
				if (e.name === 'AbortError') {
					return { success: false, method: 'save-picker', error: 'Abgebrochen' };
				}
				console.warn('Save picker failed, falling back to download:', e);
			}
		}

		return this.downloadFallback(blob, options.filename);
	}

	private downloadFallback(blob: Blob, filename: string): ExportResult {
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = filename;
		a.style.display = 'none';
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
		return { success: true, method: 'download' };
	}

	download(options: ExportOptions): ExportResult {
		const blob = this.createBlob(options.content, options.mimeType);
		return this.downloadFallback(blob, options.filename);
	}

	async share(options: ExportOptions, title?: string, text?: string): Promise<ExportResult> {
		const blob = this.createBlob(options.content, options.mimeType);
		const file = this.createFile(blob, options.filename);

		if (!this.canShareFile(file)) {
			return {
				success: false,
				method: 'share',
				error: 'Teilen wird auf diesem Gerät nicht unterstützt'
			};
		}

		try {
			await navigator.share({
				files: [file],
				title: title || options.filename,
				text: text
			});
			return { success: true, method: 'share' };
		} catch (e: any) {
			if (e.name === 'AbortError') {
				return { success: false, method: 'share', error: 'Abgebrochen' };
			}
			console.error('Share failed:', e);
			return { success: false, method: 'share', error: e.message };
		}
	}

	async saveOrShare(options: ExportOptions, shareTitle?: string, shareText?: string): Promise<ExportResult> {
		const blob = this.createBlob(options.content, options.mimeType);
		const file = this.createFile(blob, options.filename);
		const canShare = this.canShareFile(file);

		if (!canShare) {
			return this.saveWithPicker(options);
		}

		const choice = await this.showSaveShareDialog();

		if (choice === 'save') {
			return this.saveWithPicker(options);
		} else if (choice === 'share') {
			return this.share(options, shareTitle, shareText);
		}

		return { success: false, method: 'save-picker', error: 'Abgebrochen' };
	}

	/**
	 * Exportiert mehrere Dateien gebündelt:
	 * - Mobile: Alle Dateien auf einmal teilen
	 * - Desktop: Ordner wählen (showDirectoryPicker), alle Dateien reinschreiben
	 * - Fallback: Alle Downloads gleichzeitig auslösen
	 */
	async saveMultipleFiles(files: ExportOptions[], shareTitle?: string): Promise<ExportResult> {
		if (files.length === 0) {
			return { success: false, method: 'save-picker', error: 'Keine Dateien' };
		}
		if (files.length === 1) {
			return this.saveOrShare(files[0], shareTitle);
		}

		const blobs = files.map(f => this.createBlob(f.content, f.mimeType));
		const fileObjects = files.map((f, i) => this.createFile(blobs[i], f.filename));

		// Mobile: Mehrere Dateien auf einmal teilen
		const canShareAll = this.canShare && (() => {
			try { return navigator.canShare({ files: fileObjects }); } catch { return false; }
		})();

		if (canShareAll) {
			const choice = await this.showSaveShareDialog(files.length);
			if (choice === 'share') {
				try {
					await navigator.share({ files: fileObjects, title: shareTitle || 'Export' });
					return { success: true, method: 'share' };
				} catch (e: any) {
					if (e.name === 'AbortError') return { success: false, method: 'share', error: 'Abgebrochen' };
					console.warn('Share failed, falling back:', e);
				}
			} else if (choice === 'cancel') {
				return { success: false, method: 'save-picker', error: 'Abgebrochen' };
			}
			// choice === 'save' → weiter zu Directory Picker / Download
		}

		// Desktop: Ordner wählen und alle Dateien reinschreiben
		if ('showDirectoryPicker' in window) {
			try {
				const dirHandle = await (window as any).showDirectoryPicker({ mode: 'readwrite' });
				for (let i = 0; i < files.length; i++) {
					const fileHandle = await dirHandle.getFileHandle(files[i].filename, { create: true });
					const writable = await fileHandle.createWritable();
					await writable.write(blobs[i]);
					await writable.close();
				}
				return { success: true, method: 'save-picker' };
			} catch (e: any) {
				if (e.name === 'AbortError') return { success: false, method: 'save-picker', error: 'Abgebrochen' };
				console.warn('Directory picker failed, falling back to download:', e);
			}
		}

		// Fallback: Alle Dateien als Download
		for (let i = 0; i < files.length; i++) {
			this.downloadFallback(blobs[i], files[i].filename);
		}
		return { success: true, method: 'download' };
	}

	private showSaveShareDialog(fileCount?: number): Promise<'save' | 'share' | 'cancel'> {
		return new Promise((resolve) => {
			const overlay = document.createElement('div');
			overlay.style.cssText = `
				position: fixed;
				top: 0;
				left: 0;
				right: 0;
				bottom: 0;
				background: rgba(0, 0, 0, 0.5);
				display: flex;
				align-items: center;
				justify-content: center;
				z-index: 10000;
			`;

			const dialog = document.createElement('div');
			dialog.style.cssText = `
				background: white;
				border-radius: 8px;
				padding: 24px;
				min-width: 280px;
				box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
				font-family: system-ui, -apple-system, sans-serif;
			`;

			const fileLabel = fileCount && fileCount > 1
				? `${fileCount} Dateien`
				: 'die Datei';
			dialog.innerHTML = `
				<h3 style="margin: 0 0 16px 0; font-size: 18px; color: #333;">Export</h3>
				<p style="margin: 0 0 20px 0; color: #666; font-size: 14px;">Wie möchten Sie ${fileLabel} exportieren?</p>
				<div style="display: flex; flex-direction: column; gap: 10px;">
					<button id="btn-save" style="
						padding: 12px 20px;
						font-size: 14px;
						border: none;
						border-radius: 6px;
						cursor: pointer;
						background: #2196F3;
						color: white;
					">Speichern unter...</button>
					<button id="btn-share" style="
						padding: 12px 20px;
						font-size: 14px;
						border: none;
						border-radius: 6px;
						cursor: pointer;
						background: #4CAF50;
						color: white;
					">Teilen...</button>
					<button id="btn-cancel" style="
						padding: 12px 20px;
						font-size: 14px;
						border: 1px solid #ddd;
						border-radius: 6px;
						cursor: pointer;
						background: white;
						color: #666;
						margin-top: 4px;
					">Abbrechen</button>
				</div>
			`;

			overlay.appendChild(dialog);
			document.body.appendChild(overlay);

			const cleanup = () => {
				document.body.removeChild(overlay);
			};

			dialog.querySelector('#btn-save')?.addEventListener('click', () => {
				cleanup();
				resolve('save');
			});

			dialog.querySelector('#btn-share')?.addEventListener('click', () => {
				cleanup();
				resolve('share');
			});

			dialog.querySelector('#btn-cancel')?.addEventListener('click', () => {
				cleanup();
				resolve('cancel');
			});

			overlay.addEventListener('click', (e) => {
				if (e.target === overlay) {
					cleanup();
					resolve('cancel');
				}
			});
		});
	}
}
